import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useMercenary } from './MercenaryContext';

interface TutorialContextType {
  shouldShowWelcome: boolean;
  dismissWelcome: () => Promise<void>;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { mercenary } = useMercenary();
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);

  useEffect(() => {
    const checkWelcomeStatus = async () => {
      if (!user || !mercenary) return;
      try {
        const response = await axios.get(`/api/tutorial/should-show?mercenaryId=${mercenary.id}`, {
          withCredentials: true
        });
        setShouldShowWelcome(response.data.shouldShow === true);
      } catch (error) {
        // Silently ignore – welcome popup is non-critical
      }
    };

    checkWelcomeStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, mercenary?.id]);

  const dismissWelcome = async () => {
    setShouldShowWelcome(false);
    if (!mercenary) return;
    try {
      await axios.post('/api/tutorial/dismiss', { mercenaryId: mercenary.id }, {
        withCredentials: true
      });
    } catch (error) {
      // Silently ignore
    }
  };

  return (
    <TutorialContext.Provider value={{ shouldShowWelcome, dismissWelcome }}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialContext;
